import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../utils/apiAdmin';

/**
 * The audit page's data.
 *
 * ⚠️ IT LOADS A WINDOW, NOT A PAGE, AND SAYS SO. DynamoDB has no text search, so
 * free text is applied in memory - which is only honest if the caller can see
 * what was searched. The hook pages the cursor until the chosen date range is
 * exhausted or MAX_EVENTS is reached, and reports both `loaded` and `truncated`
 * so the page can state the bound rather than imply completeness.
 *
 * ⚠️ NO RESOLUTION HAPPENS HERE. Names, entity subjects and value labels all
 * arrive resolved from GET /admin/audit - the same rows the MCP history tools
 * return. A second resolution in the frontend is exactly what the shared layer
 * modules exist to prevent.
 *
 * The cost of a deep window is stated in the plan and worth repeating: 5,000
 * events at 500 per page is ten requests, each rebuilding the resolver's context
 * and each writing its own access row. Reading is recorded as reading.
 */

const PAGE_SIZE = 500;
export const MAX_EVENTS = 5000;

/** The `_system` partition - events belonging to no project. */
export const SYSTEM_SCOPE = '_system';

export function useAuditTrail({ scope, include, startDate, endDate }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [truncated, setTruncated] = useState(false);
    // Bumped to force a refetch without changing the filters.
    const [reloadToken, setReloadToken] = useState(0);
    // Guards against a slow earlier request landing after a newer one.
    const requestRef = useRef(0);

    const reload = useCallback(() => setReloadToken(t => t + 1), []);

    useEffect(() => {
        if (!scope) return undefined;

        const requestId = ++requestRef.current;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            setTruncated(false);

            const collected = [];
            let cursor = null;
            let hitCap = false;

            try {
                do {
                    const params = new URLSearchParams({ include, limit: String(PAGE_SIZE) });
                    if (startDate) params.set('startDate', startDate);
                    if (endDate) params.set('endDate', endDate);
                    if (cursor) params.set('cursor', cursor);

                    const response = await apiFetch(
                        `${process.env.REACT_APP_API_URL}/admin/audit/${encodeURIComponent(scope)}?${params}`
                    );
                    if (!response.ok) {
                        const body = await response.json().catch(() => ({}));
                        throw new Error(body.error || `Audit read failed (${response.status})`);
                    }

                    const body = await response.json();
                    collected.push(...(body.entries || []));
                    cursor = body.hasMore ? body.cursor : null;

                    if (collected.length >= MAX_EVENTS && cursor) {
                        hitCap = true;
                        break;
                    }
                } while (cursor);

                // A stale request must not overwrite a newer answer.
                if (cancelled || requestId !== requestRef.current) return;
                setEvents(collected.slice(0, MAX_EVENTS));
                setTruncated(hitCap);
            } catch (err) {
                if (cancelled || requestId !== requestRef.current) return;
                console.error('Audit trail load failed:', err);
                setError(err.message);
                setEvents([]);
            } finally {
                if (!cancelled && requestId === requestRef.current) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [scope, include, startDate, endDate, reloadToken]);

    return { events, loading, error, truncated, reload };
}

export default useAuditTrail;
